/**
 * UnsavedChangesGuard -- MSR QA notes 2026-10-19, item 8: "save entered data before the user moves to another page".
 *
 * A prompt, not autosave: the MSR forms it guards make final or commission-relevant writes (record a sale, close a
 * sales event, time off), so leaving must be a choice, never a silent save. Render it inside a form with `when` =
 * "the form has something typed that is not saved":
 *   - in-app navigation (links, Back, the tab bar) is held by React Router's useBlocker and answered in a small dialog;
 *   - a reload / tab close gets the browser's own "Leave site?" prompt (beforeunload).
 *
 * useBlocker only exists under a data router (createBrowserRouter, which the app uses). Under a plain <MemoryRouter>
 * (unit tests) the in-app part is skipped and only beforeunload applies, so components stay testable as they were.
 */

import React, { useContext, useEffect } from "react";
import { UNSAFE_DataRouterContext, useBlocker } from "react-router-dom";

interface Props {
  when: boolean;
  message?: string;
}

const DEFAULT_MESSAGE =
  "You have changes that aren't saved. Leave this page and lose them?";

const BlockerDialog: React.FC<Props> = ({
  when,
  message = DEFAULT_MESSAGE,
}) => {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      when &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search),
  );

  // A guard that stops applying (saved / cleared) must release a navigation it was holding.
  useEffect(() => {
    if (!when && blocker.state === "blocked") {
      blocker.reset();
    }
  }, [when, blocker]);

  if (blocker.state !== "blocked") {
    return null;
  }
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="unsaved-changes-title"
      data-testid="unsaved-changes-dialog"
    >
      <div className="w-full max-w-sm space-y-3 rounded-lg bg-white p-4 shadow-lg">
        <h2
          id="unsaved-changes-title"
          className="text-base font-semibold text-gray-900"
        >
          Unsaved changes
        </h2>
        <p className="text-sm text-gray-700">{message}</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => blocker.reset()}
            className="flex-1 min-h-[44px] rounded-md bg-primary-600 px-3 text-sm font-semibold text-white"
          >
            Stay on this page
          </button>
          <button
            type="button"
            onClick={() => blocker.proceed()}
            className="flex-1 min-h-[44px] rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700"
          >
            Leave without saving
          </button>
        </div>
      </div>
    </div>
  );
};

const UnsavedChangesGuard: React.FC<Props> = ({ when, message }) => {
  const dataRouter = useContext(UNSAFE_DataRouterContext);

  useEffect(() => {
    if (!when) {
      return;
    }
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chrome needs returnValue set; the text itself is not shown by modern browsers.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [when]);

  return dataRouter ? <BlockerDialog when={when} message={message} /> : null;
};

export default UnsavedChangesGuard;
